import { useEffect, useState } from 'react';
import { useMap, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import '@maplibre/maplibre-gl-leaflet';

// OpenFreeMap serves the Positron style with no API key, no account and no
// usage cap. CARTO's basemaps.cartocdn.com tiles now render an "API KEY
// REQUIRED" watermark for unauthenticated use, which is why we moved off them.
const BASEMAP_STYLE = 'https://tiles.openfreemap.org/styles/positron';

const ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://openfreemap.org/">OpenFreeMap</a>';

// Raster fallback for browsers without WebGL, or if OpenFreeMap is unreachable.
// Esri's legacy tile service is also keyless.
const FALLBACK_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}';
const FALLBACK_ATTRIBUTION = 'Tiles &copy; Esri';

const BaseMapLayer = () => {
  const map = useMap();
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    if (useFallback) return undefined;

    let layer;
    try {
      layer = L.maplibreGL({
        style: BASEMAP_STYLE,
        attribution: ATTRIBUTION,
        // The flight overlays are plain Leaflet layers drawn above this one.
        interactive: false,
      });
      layer.addTo(map);

      const glMap = layer.getMaplibreMap();
      glMap.on('error', (e) => {
        // Style/tile failures leave a blank canvas, so drop to raster instead.
        if (e?.error?.status >= 400 || e?.error?.message) {
          console.warn('OpenFreeMap basemap failed, falling back to raster tiles', e.error);
          setUseFallback(true);
        }
      });
    } catch (err) {
      console.warn('MapLibre unavailable (no WebGL?), falling back to raster tiles', err);
      setUseFallback(true);
      return undefined;
    }

    return () => {
      if (layer && map.hasLayer(layer)) map.removeLayer(layer);
    };
  }, [map, useFallback]);

  if (useFallback) {
    return <TileLayer url={FALLBACK_URL} attribution={FALLBACK_ATTRIBUTION} maxZoom={16} />;
  }

  return null;
};

// Referenced so bundlers keep maplibre-gl even though the Leaflet bridge is the
// only consumer of it.
export const maplibre = maplibregl;

export default BaseMapLayer;
